const img = (n) => `/lp-images/${n}.jpg`;

export const CASE_ACCENTS = {
  auto: "#f5b700",
  truck: "#e03a3a",
  rideshare: "#10c46a",
  motorcycle: "#ff9100",
  injury: "#3a86ff",
};

// Buyer brand color, unless it's too dark to read on the navy theme.
export function pickAccent(brand, slug) {
  const fallback = CASE_ACCENTS[slug] || CASE_ACCENTS.injury;
  const m = /^#?([0-9a-f]{6})$/i.exec(brand || "");
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum < 0.3 ? fallback : `#${m[1]}`;
}

// Vertical funnels know their case type up front; the all-in-one asks.
export const CASE_PRESET_TYPE = { auto: "auto", truck: "truck", rideshare: "rideshare", motorcycle: "motorcycle", injury: "" };

export const FIRST_STEP = {
  auto: {
    field: "detail",
    detailLabel: "Crash type",
    title: "What kind of crash was it?",
    subtitle: "Tap the closest match.",
    options: [
      { value: "rear_ended", label: "I was rear-ended", img: img("card-car") },
      { value: "intersection", label: "Hit at an intersection", img: img("card-carnight") },
      { value: "passenger", label: "I was a passenger", img: img("card-passenger") },
      { value: "other", label: "Something else" },
    ],
  },
  truck: {
    field: "detail",
    detailLabel: "Truck type",
    title: "What kind of truck was involved?",
    subtitle: "Tap the closest match.",
    options: [
      { value: "semi", label: "Semi / 18-wheeler", img: img("card-semi") },
      { value: "box", label: "Delivery or box truck", img: img("card-boxtruck") },
      { value: "dump", label: "Dump or construction truck", img: img("card-dumptruck") },
      { value: "commercial", label: "Other work vehicle", img: img("card-van") },
    ],
  },
  rideshare: {
    field: "detail",
    detailLabel: "Rideshare role",
    title: "What was your role in the crash?",
    subtitle: "Every role can have a claim.",
    options: [
      { value: "passenger", label: "I was a passenger", img: img("card-passenger") },
      { value: "driver", label: "I was the Uber / Lyft driver", img: img("card-driver") },
      { value: "hit_by", label: "A rideshare car hit me", img: img("card-carnight") },
      { value: "walking", label: "I was walking or biking", img: img("card-pedestrian") },
    ],
  },
  motorcycle: {
    field: "detail",
    detailLabel: "Crash type",
    title: "What happened on your ride?",
    subtitle: "Tap the closest match.",
    options: [
      { value: "hit_by_vehicle", label: "A car or truck hit me", img: img("card-car") },
      { value: "road_hazard", label: "Road hazard or debris", img: img("card-pothole") },
      { value: "passenger", label: "I was riding as a passenger", img: img("card-bikepassenger") },
      { value: "other", label: "Something else", img: img("card-motorcycle") },
    ],
  },
  injury: {
    field: "case_type",
    title: "What happened to you?",
    subtitle: "Pick the one that fits best.",
    options: [
      { value: "auto", label: "Car accident", img: img("card-car") },
      { value: "truck", label: "Truck / 18-wheeler", img: img("card-semi") },
      { value: "motorcycle", label: "Motorcycle", img: img("card-motorcycle") },
      { value: "rideshare", label: "Uber / Lyft", img: img("card-rideshare") },
      { value: "pedestrian", label: "Hit while walking", img: img("card-pedestrian") },
      { value: "bicycle", label: "Bicycle accident", img: img("card-bicycle") },
      { value: "slip_fall", label: "Slip & fall", img: img("card-slip") },
      { value: "dog_bite", label: "Dog bite", img: img("card-dog") },
      { value: "medical_malpractice", label: "Medical mistake", img: img("card-medical") },
      { value: "other", label: "Something else" },
    ],
  },
};

export const AGE_OPTIONS = [
  { value: "within_30_days", label: "Within the last 30 days" },
  { value: "31_to_60_days", label: "1–2 months ago" },
  { value: "61_to_90_days", label: "2–3 months ago" },
  { value: "91_days_to_1_year", label: "3–12 months ago" },
  { value: "over_1_year", label: "Over a year ago" },
];

export const HOW_IT_WORKS = [
  { title: "Tap through the quiz", body: "About a minute. No paperwork and no cost." },
  { title: "An attorney reviews it", body: "Your answers go to an injury attorney who handles cases like yours." },
  { title: "Get a call or text", body: "Talk through your options — free and with no obligation." },
];

const COMMON_FAQ = [
  {
    q: "Does this cost anything?",
    a: "No. The case review is free and there's no obligation. Injury attorneys usually work on contingency, which means no fee unless they recover money for you.",
  },
  {
    q: "Who will contact me?",
    a: "An intake specialist or attorney will call or text the number you give us. Keep your phone nearby so you don't miss them.",
  },
];

export const CASE_FAQ = {
  auto: [
    {
      q: "The insurance company already called me. What should I do?",
      a: "You don't have to give a recorded statement or accept the first offer. An attorney can deal with the insurer for you and make sure your medical bills and lost pay are counted.",
    },
    ...COMMON_FAQ,
  ],
  truck: [
    {
      q: "Why are truck cases different?",
      a: "They can involve the driver, the trucking company, its insurer and federal safety rules. Evidence like logbooks and black-box data matters, so it helps to act quickly.",
    },
    ...COMMON_FAQ,
  ],
  rideshare: [
    {
      q: "Who pays in an Uber or Lyft crash?",
      a: "It depends on what the app was doing at the time. Coverage can come from the driver's own policy and the rideshare company's insurance — an attorney can sort out which applies.",
    },
    ...COMMON_FAQ,
  ],
  motorcycle: [
    {
      q: "What if they say it was my fault?",
      a: "Riders are often blamed unfairly. Many states still let you recover even if you were partly at fault, and an attorney can push back on the other side's story.",
    },
    ...COMMON_FAQ,
  ],
  injury: [
    {
      q: "What kinds of cases qualify?",
      a: "Car, truck, motorcycle and rideshare crashes, pedestrian and bike accidents, slip & falls, dog bites, medical mistakes and more. The quiz helps us find the right attorney.",
    },
    ...COMMON_FAQ,
  ],
};
